// src/pages/auth/Agreements.jsx
import { useMemo, useEffect, useState, useRef, useCallback } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  Row,
  Col,
  Card,
  CardBody,
  CardHeader,
  Container,
  Form,
  Input,
  Label,
  Alert,
} from "reactstrap";
import toastr from "toastr";
import withRouter from "../../components/Common/withRouter";
import SignaturePad from "../../components/Common/SignaturePad";
import { withTranslation } from "react-i18next";
import { useWorkerSubmitAgreement } from "../../api/auth";
import CustomHeader from "../../components/Auth/CustomHeader";
import workerAgreementPdfUrl from "../../assets/pdfs/worker-agreement.pdf?url";
import {
  buildSignedContractPdf,
  fetchPdfAsArrayBuffer,
  WORKER_AGREEMENT_SIGNATURE,
} from "../../utils/signedContractPdf";
import PropTypes from "prop-types";
import {
  JOBARK_PRIVACY_URL,
  WORKER_ENGAGEMENT_AGREEMENT_PATH,
} from "../../constants/workerOnboarding";

/** Multipart payload: JSON strings + declarationProof PDF file. */
function buildWorkerAgreementFormData({ email, values, declarationProofFile }) {
  const fd = new FormData();
  if (email) fd.append("email", email);
  fd.append(
    "declarationsConsents",
    JSON.stringify({
      infoAccurate: values.infoAccurate,
      contractAndPayrollAgreed: values.contractAndPayrollAgreed,
      privacyConsent: values.privacyConsent,
      workingTimeAgreement: values.workingTimeAgreement,
      termsAndPrivacyAccepted: !!values.termsAndPrivacyAccepted,
      hasAcceptedWorkerEngagementAgreement:
        !!values.hasAcceptedWorkerEngagementAgreement,
    })
  );
  fd.append("declarationProof", declarationProofFile);
  return fd;
}

const WorkerAgreement = (props) => {
  const { t } = props;
  document.title = t("agreementsTitle");

  // read ?email=... from URL
  const search = props?.router?.location?.search || "";
  const qp = useMemo(() => new URLSearchParams(search), [search]);
  const qpEmail = qp.get("email");

  const [onboarded, setOnboarded] = useState(false);
  const [signatureDrawn, setSignatureDrawn] = useState(false);
  const [signatureError, setSignatureError] = useState(false);
  const signaturePadRef = useRef(null);
  const [contractTemplateBuffer, setContractTemplateBuffer] = useState(null);
  const [contractTemplateStatus, setContractTemplateStatus] = useState(
    /** @type {"loading" | "ready" | "error"} */ ("loading")
  );

  const workerAgreementMutation = useWorkerSubmitAgreement();

  const reloadContractTemplate = useCallback(() => {
    setContractTemplateStatus("loading");
    fetchPdfAsArrayBuffer(workerAgreementPdfUrl)
      .then((buf) => {
        setContractTemplateBuffer(buf);
        setContractTemplateStatus("ready");
      })
      .catch(() => {
        setContractTemplateBuffer(null);
        setContractTemplateStatus("error");
      });
  }, []);

  useEffect(() => {
    reloadContractTemplate();
  }, [reloadContractTemplate]);

  const handleSignatureChange = useCallback((hasSig) => {
    setSignatureDrawn(hasSig);
    if (hasSig) setSignatureError(false);
  }, []);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      infoAccurate: false,
      contractAndPayrollAgreed: false,
      privacyConsent: false,
      workingTimeAgreement: false,
      termsAndPrivacyAccepted: false,
      hasAcceptedWorkerEngagementAgreement: false,
    },
    validationSchema: Yup.object({
      infoAccurate: Yup.boolean().oneOf([true], t("agreementsRequired")),
      contractAndPayrollAgreed: Yup.boolean().oneOf(
        [true],
        t("agreementsRequired")
      ),
      privacyConsent: Yup.boolean().oneOf([true], t("agreementsRequired")),
      workingTimeAgreement: Yup.boolean().oneOf(
        [true],
        t("agreementsRequired")
      ),
      termsAndPrivacyAccepted: Yup.boolean().oneOf(
        [true],
        t("agreementsPlatformTermsRequired")
      ),
      hasAcceptedWorkerEngagementAgreement: Yup.boolean().oneOf(
        [true],
        t("agreementsEngagementAgreementRequired")
      ),
    }),
    onSubmit: async (values) => {
      if (!signatureDrawn) {
        setSignatureError(true);
        return;
      }
      setSignatureError(false);

      let buffer = contractTemplateBuffer;
      if (!buffer || contractTemplateStatus !== "ready") {
        try {
          buffer = await fetchPdfAsArrayBuffer(workerAgreementPdfUrl);
          setContractTemplateBuffer(buffer);
          setContractTemplateStatus("ready");
        } catch {
          toastr.error(t("agreementsSubmitError"));
          return;
        }
      }

      const canvas = signaturePadRef.current?.getCanvas();
      let declarationProofFile;
      try {
        declarationProofFile = await buildSignedContractPdf(
          canvas,
          buffer,
          WORKER_AGREEMENT_SIGNATURE
        );
      } catch {
        declarationProofFile = null;
      }
      if (!declarationProofFile) {
        toastr.error(t("agreementsSubmitError"));
        return;
      }

      const fd = buildWorkerAgreementFormData({
        email: qpEmail,
        values,
        declarationProofFile,
      });

      workerAgreementMutation.mutate(fd, {
        onSuccess: () => {
          toastr.success(t("agreementsSubmitSuccess"));
          setOnboarded(true);
        },
        onError: () => {
          // Error toast is handled by global axios interceptor
        },
      });
    },
  });

  const errorFor = (key) =>
    formik.touched[key] && formik.errors[key] ? (
      <div className="invalid-feedback d-block">{formik.errors[key]}</div>
    ) : null;

  return (
    <>
      <CustomHeader />

      {/* Unified page template */}
      <div className="account-pages my-5 pt-sm-5">
        <Container>
          {/* One Card for both states */}
          <Row className="justify-content-center">
            <Col md={10} lg={8} xl={7}>
              <Card
                className={onboarded ? "border-0 shadow-sm" : "overflow-hidden"}
              >
                <CardHeader className="bg-primary">
                  <h4 className="text-white mb-0 text-center py-2">
                    {onboarded ? t("confirmationTitle") : t("agreementsHeader")}
                  </h4>
                </CardHeader>

                <CardBody className={onboarded ? "p-4 p-md-5" : undefined}>
                  {onboarded ? (
                    // ===== Confirmation content =====
                    <>
                      <p className="mb-2 text-center">
                        {t("confirmThanksLine1")}
                      </p>
                      <p className="mb-2 text-center">
                        {t("confirmThanksLine2")}
                      </p>
                      <p className="mb-4 text-center">
                        {t("confirmThanksLine3")}
                      </p>
                      <p className="mb-0 text-center">
                        {t("confirmFurtherInfo1")}
                      </p>
                    </>
                  ) : (
                    // ===== Form content =====
                    <Form
                      onSubmit={(e) => {
                        e.preventDefault();
                        formik.handleSubmit();
                        return false;
                      }}
                    >
                      {formik.submitCount > 0 && !formik.isValid && (
                        <Alert color="warning" className="mb-3">
                          {t("agreementsFixAll")}
                        </Alert>
                      )}

                      <div className="mb-4">
                        <p className="mb-2">{t("agreementsIntroLine1")}</p>
                        <p className="mb-0">{t("agreementsIntroLine2")}</p>
                      </div>

                      {/* 1) Info Accurate */}
                      <div className="mb-3">
                        <div className="form-check">
                          <Input
                            id="infoAccurate"
                            type="checkbox"
                            className="form-check-input"
                            name="infoAccurate"
                            checked={formik.values.infoAccurate}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                          />
                          <Label
                            className="form-check-label"
                            htmlFor="infoAccurate"
                          >
                            {t("agreementsInfoAccurate")}{" "}
                            <span className="text-danger">*</span>
                          </Label>
                        </div>
                        {errorFor("infoAccurate")}
                      </div>

                      {/* 2) Right to work */}
                      <div className="mb-3">
                        <div className="form-check">
                          <Input
                            id="contractAndPayrollAgreed"
                            type="checkbox"
                            className="form-check-input"
                            name="contractAndPayrollAgreed"
                            checked={formik.values.contractAndPayrollAgreed}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                          />
                          <Label
                            className="form-check-label"
                            htmlFor="contractAndPayrollAgreed"
                          >
                            {t("agreementsContractAndPayrollAgreed")}{" "}
                            <span className="text-danger">*</span>
                          </Label>
                        </div>
                        {errorFor("contractAndPayrollAgreed")}
                      </div>

                      {/* 3) Keep profile up to date */}
                      <div className="mb-3">
                        <div className="form-check">
                          <Input
                            id="workingTimeAgreement"
                            type="checkbox"
                            className="form-check-input"
                            name="workingTimeAgreement"
                            checked={formik.values.workingTimeAgreement}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                          />
                          <Label
                            className="form-check-label"
                            htmlFor="workingTimeAgreement"
                          >
                            {t("agreementsWorkingTimeAgreement")}{" "}
                            <span className="text-danger">*</span>
                          </Label>
                        </div>
                        {errorFor("workingTimeAgreement")}
                      </div>

                      {/* 4) Privacy Policy */}
                      <div className="mb-3">
                        <div className="form-check">
                          <Input
                            id="privacyConsent"
                            type="checkbox"
                            className="form-check-input"
                            name="privacyConsent"
                            checked={formik.values.privacyConsent}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                          />
                          <Label
                            className="form-check-label"
                            htmlFor="privacyConsent"
                          >
                            {t("agreementsGdprConsentPrefix")}{" "}
                            <a
                              href={JOBARK_PRIVACY_URL}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(e) => e.stopPropagation()}
                            >
                              {t("agreementsGdprConsentLink")}
                            </a>
                            {t("agreementsGdprConsentSuffix")}{" "}
                            <span className="text-danger">*</span>
                          </Label>
                        </div>
                        {errorFor("privacyConsent")}
                      </div>

                      {contractTemplateStatus === "error" && (
                        <Alert color="danger" className="mb-3">
                          {t("agreementsContractLoadFailed")}{" "}
                          <button
                            type="button"
                            className="btn btn-link p-0 align-baseline"
                            onClick={reloadContractTemplate}
                          >
                            {t("agreementsContractRetry")}
                          </button>
                        </Alert>
                      )}

                      {contractTemplateStatus === "loading" && (
                        <p className="text-muted small mb-4">{t("loading")}</p>
                      )}

                      {contractTemplateStatus === "ready" && (
                        <div className="mb-4">
                          <h6 className="text-muted small text-uppercase mb-2">
                            {t("agreementsContractPreview")}
                          </h6>
                          <div className="border rounded overflow-hidden bg-light">
                            <iframe
                              title={t("agreementsContractPreview")}
                              src={workerAgreementPdfUrl}
                              className="d-block w-100 border-0"
                              style={{ height: 700 }}
                            />
                          </div>
                        </div>
                      )}

                      <SignaturePad
                        ref={signaturePadRef}
                        className="mb-4"
                        label={t("agreementsSignatureLabel")}
                        hint={t("agreementsSignatureHint")}
                        clearLabel={t("agreementsSignatureClear")}
                        ariaLabel={t("agreementsSignatureLabel")}
                        showError={signatureError}
                        errorMessage={t("agreementsSignatureRequired")}
                        onSignatureChange={handleSignatureChange}
                      />

                      {/* 5) Platform Terms of Use */}
                      <div className="mb-3">
                        <div className="form-check">
                          <Input
                            id="termsAndPrivacyAccepted"
                            type="checkbox"
                            className="form-check-input"
                            name="termsAndPrivacyAccepted"
                            checked={formik.values.termsAndPrivacyAccepted}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                          />
                          <Label
                            className="form-check-label"
                            htmlFor="termsAndPrivacyAccepted"
                          >
                            {t("agreementsPlatformTermsAccepted")}{" "}
                            <span className="text-danger">*</span>
                          </Label>
                        </div>
                        {errorFor("termsAndPrivacyAccepted")}
                      </div>

                      {/* 6) Worker Engagement Agreement */}
                      <div className="mb-4">
                        <div className="form-check">
                          <Input
                            id="hasAcceptedWorkerEngagementAgreement"
                            type="checkbox"
                            className="form-check-input"
                            name="hasAcceptedWorkerEngagementAgreement"
                            checked={
                              formik.values
                                .hasAcceptedWorkerEngagementAgreement
                            }
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                          />
                          <Label
                            className="form-check-label"
                            htmlFor="hasAcceptedWorkerEngagementAgreement"
                          >
                            {t("agreementsEngagementAgreementPrefix")}{" "}
                            <a
                              href={WORKER_ENGAGEMENT_AGREEMENT_PATH}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(e) => e.stopPropagation()}
                            >
                              {t("agreementsEngagementAgreementLink")}
                            </a>
                            {t("agreementsEngagementAgreementSuffix")}{" "}
                            <span className="text-danger">*</span>
                          </Label>
                        </div>
                        {errorFor("hasAcceptedWorkerEngagementAgreement")}
                      </div>

                      <div className="d-grid">
                        <button
                          type="submit"
                          className="btn btn-primary"
                          disabled={
                            workerAgreementMutation.isPending ||
                            contractTemplateStatus === "loading" ||
                            contractTemplateStatus === "error"
                          }
                        >
                          {t("agreementsSubmit")}
                        </button>
                      </div>
                    </Form>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </>
  );
};

WorkerAgreement.propTypes = {
  t: PropTypes.func.isRequired,
  router: PropTypes.object.isRequired,
};

export default withRouter(withTranslation()(WorkerAgreement));
